// ============================================================================
// TASK FOR OWNER/TEAM RUNNER
// Loads a task by id + propertyId and checks if current user can execute it:
// - Owner: task.assigned_type === 'owner' && task.assignee_user_id === user.id && property.owner_id === user.id
// - Team: task.assigned_type === 'team' && task.assignee_user_id === user.id && can_execute_tasks on property_access
// - Direct cleaner: task.cleaner_id matches cleaners.id for auth user (assigned_type cleaner / unassigned / null)
// - Checklist assignee: row in cleaning_task_checklist_assignments by assignee_user_id OR cleaner_id + can_execute_tasks
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { CurrentCleaningTask } from "@/types/cleaning";
import type { Json } from "@/integrations/supabase/types";
import { resolveJobLeaveOutStaging } from "@/lib/guestAmenityStaging";
import { useWithPendingStamps } from "@/lib/stampOutbox";
import { withTurnoverClock, type ReservationDateRow } from "@/lib/turnoverWindow";

export interface TaskForRunnerResult {
  task: CurrentCleaningTask | null;
  allowed: boolean;
  errorMessage?: string;
  isLoading: boolean;
}

function shapeTask(
  raw: Record<string, unknown>,
  assignedChecklists?: { checklist_id: string; checklist_name: string }[],
  leaveOutStaging?: CurrentCleaningTask["checklist_guest_amenity_staging"],
): CurrentCleaningTask {
  const checklists = raw.checklists as {
    name?: string;
    access_verification_settings?: unknown;
    guest_amenities_instructions?: string | null;
    guest_amenity_staging?: unknown;
  } | null;
  const properties = raw.properties as { name?: string; address?: string | null } | null;
  const reservations = raw.reservations as {
    guest_name?: string | null;
    guest_count?: number | null;
    check_in_time?: string | null;
    check_out_time?: string | null;
  } | null;
  const base: CurrentCleaningTask = {
    id: raw.id as string,
    property_id: raw.property_id as string,
    status: raw.status as string,
    scheduled_date: raw.scheduled_date as string,
    checklist_completed: raw.checklist_completed as boolean | null,
    inventory_completed: raw.inventory_completed as boolean | null,
    checklist_id: raw.checklist_id as string | null,
    checklist_name: checklists?.name ?? null,
    checklist_access_verification_settings: checklists?.access_verification_settings ?? null,
    checklist_guest_amenities_instructions:
      (checklists?.guest_amenities_instructions && String(checklists.guest_amenities_instructions).trim()) || null,
    checklist_guest_amenity_staging: leaveOutStaging ?? null,
    entry_walkthrough_completed: Boolean(raw.entry_walkthrough_completed),
    inventory_guest_phase_completed: Boolean(raw.inventory_guest_phase_completed),
    inventory_supply_phase_completed: Boolean(raw.inventory_supply_phase_completed),
    assigned_checklists: assignedChecklists ?? null,
    include_inventory_check: (raw.include_inventory_check as boolean) !== false,
    property_name: properties?.name ?? null,
    property_address: properties?.address ?? null,
    is_test: Boolean(raw.is_test),
    test_setup: raw.test_setup ?? null,
    started_at: raw.started_at as string | null,
    start_location_lat: (raw.start_location_lat as number | null) ?? null,
    start_location_override_reason: (raw.start_location_override_reason as string | null) ?? null,
    completed_at: raw.completed_at as string | null,
    duration_minutes: raw.duration_minutes as number | null,
    extra_dirty: raw.extra_dirty as boolean | null,
    extra_dirty_notes: raw.extra_dirty_notes as string | null,
    unit_empty_on_arrival: raw.unit_empty_on_arrival as boolean | null,
    early_cleaning_started: raw.early_cleaning_started as boolean | null,
    guest_name: reservations?.guest_name ?? null,
    guest_count: reservations?.guest_count ?? (typeof raw.guest_count === "number" ? raw.guest_count : null) ?? null,
    override_check_in_time: raw.override_check_in_time as string | null,
    override_check_out_time: raw.override_check_out_time as string | null,
    reservation_check_in_time: reservations?.check_in_time ?? null,
    reservation_check_out_time: reservations?.check_out_time ?? null,
    entry_walkthrough_state: (raw.entry_walkthrough_state as Json | null) ?? null,
    guest_setup_completed: Boolean(raw.guest_setup_completed),
    guest_setup_state: (raw.guest_setup_state as Json | null) ?? null,
    reservation_id: (raw.reservation_id as string | null) ?? null,
  };
  return withTurnoverClock(base, (raw._clockRows as ReservationDateRow[] | undefined) ?? []);
}

export function useTaskForOwnerOrTeamRunner(
  propertyId: string | undefined,
  taskId: string | undefined
): TaskForRunnerResult {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["task-for-owner-team-runner", propertyId, taskId, user?.id],
    queryFn: async (): Promise<{ task: CurrentCleaningTask | null; allowed: boolean; errorMessage?: string }> => {
      if (!propertyId || !taskId || !user?.id) {
        return { task: null, allowed: false, errorMessage: "Missing property, task, or user." };
      }

      // 1) Fetch task row only (no embedded relations) to avoid 400 from join/RLS.
      // A hiccup (timeout, dropped signal) is retried and reported as such; only a truly missing row is "not found".
      const taskCols =
        "id, property_id, status, scheduled_date, checklist_completed, inventory_completed, inventory_guest_phase_completed, inventory_supply_phase_completed, checklist_id, include_inventory_check, started_at, completed_at, duration_minutes, extra_dirty, extra_dirty_notes, unit_empty_on_arrival, early_cleaning_started, override_check_in_time, override_check_out_time, assigned_type, assignee_user_id, cleaner_id, reservation_id, entry_walkthrough_completed, entry_walkthrough_state, guest_setup_completed, guest_setup_state, guest_count";
      let taskOnly: unknown = null;
      let taskErr: { message?: string } | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const res = await supabase.from("cleaning_tasks").select(taskCols).eq("id", taskId).maybeSingle();
        taskOnly = res.data;
        taskErr = res.error;
        if (!res.error) break;
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }

      if (taskErr) {
        return {
          task: null,
          allowed: false,
          errorMessage: `Couldn’t load this task${taskErr.message ? ` (${taskErr.message})` : ""}. Tap Retry.`,
        };
      }
      if (!taskOnly) {
        return { task: null, allowed: false, errorMessage: "Task not found or access denied." };
      }

      const taskRow = taskOnly as Record<string, unknown>;

      const { data: myCleanerRow } = await supabase.from("cleaners").select("id").eq("user_id", user.id).maybeSingle();
      const myCleanerId = (myCleanerRow as { id?: string } | null)?.id ?? null;

      // 2) Fetch relations separately to avoid 400
      let checklists: {
        id: string;
        name: string;
        access_verification_settings?: unknown;
        guest_amenities_instructions?: string | null;
        guest_amenity_staging?: unknown;
      } | null = null;
      let properties: { id: string; name: string; address: string | null; owner_id: string } | null = null;
      let reservations: { guest_name: string | null; guest_count: number | null; check_in_time: string | null; check_out_time: string | null } | null = null;

      if (taskRow.checklist_id) {
        const { data: cl } = await supabase
          .from("checklists")
          .select("id, name, access_verification_settings, guest_amenities_instructions, guest_amenity_staging")
          .eq("id", taskRow.checklist_id as string)
          .maybeSingle();
        if (cl) checklists = cl as typeof checklists;
      }
      const taskPropertyId = taskRow.property_id as string;
      const { data: prop } = await supabase
        .from("properties")
        .select("id, name, address, owner_id")
        .eq("id", taskPropertyId)
        .maybeSingle();
      if (prop) properties = prop as typeof properties;
      const reservationId = taskRow.reservation_id as string | null;
      if (reservationId) {
        const { data: res } = await supabase.from("reservations").select("guest_name, guest_count, check_in_time, check_out_time, early_checkout_detected").eq("id", reservationId).maybeSingle();
        if (res) reservations = res as typeof reservations;
      }

      const { data: clockRows } = await supabase
        .from("reservations")
        .select("id, start_date, end_date, guest_name, guest_count, check_in_time, check_out_time, early_checkout_detected, reservation_type")
        .eq("property_id", taskPropertyId);

      (taskRow as Record<string, unknown>).checklists = checklists;
      (taskRow as Record<string, unknown>).properties = properties;
      (taskRow as Record<string, unknown>).reservations = reservations;
      (taskRow as Record<string, unknown>)._clockRows = clockRows ?? [];

      const isPropertyOwner = !!properties && properties.owner_id === user.id;

      const assignedType = (taskRow as { assigned_type?: string | null }).assigned_type;
      const assigneeUserId = (taskRow as { assignee_user_id?: string | null }).assignee_user_id;
      const taskCleanerId = (taskRow as { cleaner_id?: string | null }).cleaner_id ?? null;
      const taskStatus = (taskRow as { status?: string }).status;
      const checklistCompleted = !!(taskRow as { checklist_completed?: boolean | null }).checklist_completed;
      const inventoryCompleted = !!(taskRow as { inventory_completed?: boolean | null }).inventory_completed;
      const isResumableInProgress = taskStatus === "in_progress" && !(checklistCompleted && inventoryCompleted);

      // Fetch assigned checklists for this user (assignee_user_id or cleaner_id on junction rows)
      let assignmentQuery = supabase
        .from("cleaning_task_checklist_assignments")
        .select("checklist_id")
        .eq("cleaning_task_id", taskId);
      if (myCleanerId) {
        assignmentQuery = assignmentQuery.or(`assignee_user_id.eq.${user.id},cleaner_id.eq.${myCleanerId}`);
      } else {
        assignmentQuery = assignmentQuery.eq("assignee_user_id", user.id);
      }
      const { data: assignmentRows } = await assignmentQuery;
      let assignedChecklists: { checklist_id: string; checklist_name: string }[] | undefined;
      if (assignmentRows?.length) {
        const names = await Promise.all(
          assignmentRows.map((r) => supabase.from("checklists").select("name").eq("id", r.checklist_id).maybeSingle())
        );
        assignedChecklists = assignmentRows.map((r, i) => ({
          checklist_id: r.checklist_id,
          checklist_name: (names[i]?.data as { name?: string } | null)?.name ?? "",
        }));
      }

      const rowForShape = { ...taskRow } as Record<string, unknown>;
      const leaveOutStaging = await resolveJobLeaveOutStaging(
        taskPropertyId,
        checklists?.guest_amenity_staging,
      );
      const shaped = () => shapeTask(rowForShape, assignedChecklists, leaveOutStaging);

      type AccessMember = {
        grantee_user_id?: string | null;
        grantee_type?: string | null;
        grantee_company_id?: string | null;
        can_execute_tasks?: unknown;
      };

      const loadMyCompanyIds = async () => {
        const { data: companies } = await supabase.from("cleaning_companies").select("id").eq("admin_user_id", user.id);
        return new Set((companies ?? []).map((c) => c.id));
      };

      const memberAllowsExecute = (members: AccessMember[], myCompanyIds: Set<string>) =>
        members.some(
          (m) =>
            Boolean(m.can_execute_tasks) &&
            ((m.grantee_user_id === user.id) ||
              (m.grantee_type === "company" && !!m.grantee_company_id && myCompanyIds.has(m.grantee_company_id)))
        );

      /** RPC members + fallback SELECT own property_access row (covers RPC/parse edge cases). */
      const currentUserCanExecuteTasks = async (): Promise<boolean> => {
        // Slice 22 cohosts: Tasks module on property_cohosts.permissions
        const { data: accessRow } = await supabase.rpc("get_my_property_access_level", {
          p_property_id: propertyId,
        });
        const row = Array.isArray(accessRow) ? accessRow[0] : accessRow;
        const perms = (row as { permissions?: unknown } | null)?.permissions;
        if (Array.isArray(perms) && perms.includes("tasks")) return true;

        const myCompanyIds = await loadMyCompanyIds();
        const { data: accessData, error: rpcErr } = await supabase.rpc("get_property_access_and_invites", {
          p_property_id: propertyId,
        });
        let members: AccessMember[] = [];
        if (!rpcErr && accessData && typeof accessData === "object") {
          const raw = (accessData as { members?: unknown }).members;
          if (Array.isArray(raw)) members = raw as AccessMember[];
        }
        if (memberAllowsExecute(members, myCompanyIds)) return true;
        const { data: pa, error: paErr } = await supabase
          .from("property_access")
          .select("can_execute_tasks")
          .eq("property_id", propertyId)
          .eq("grantee_type", "user")
          .eq("grantee_user_id", user.id)
          .maybeSingle();
        if (paErr || !pa) return false;
        return Boolean((pa as { can_execute_tasks?: boolean }).can_execute_tasks);
      };

      // Owner can execute when task is assigned to them (assigned_type owner, assignee_user_id = owner)
      if (isPropertyOwner && assignedType === "owner" && assigneeUserId === user.id) {
        return { task: shaped(), allowed: true };
      }

      // Direct cleaner on task row (invited worker / company cleaner), including legacy unassigned+cleaner_id
      if (myCleanerId && taskCleanerId === myCleanerId) {
        const at = assignedType ?? "";
        if (!at || at === "cleaner" || at === "unassigned") {
          return { task: shaped(), allowed: true };
        }
      }

      // Direct team assignee on task
      if (assignedType === "team" && assigneeUserId === user.id) {
        // If this task is already in progress and assigned to this user, always allow resume.
        // This preserves task continuation after app relaunch/login even when can_execute_tasks is currently false.
        if (isResumableInProgress) {
          return { task: shaped(), allowed: true };
        }
        const hasCanExecute = await currentUserCanExecuteTasks();
        if (hasCanExecute) {
          return { task: shaped(), allowed: true };
        }
        return {
          task: shaped(),
          allowed: false,
          errorMessage: "You don't have permission to complete this task. Ask the owner to enable 'Can do tasks' for you.",
        };
      }

      // Assigned via cleaning_task_checklist_assignments (user or cleaner on rows) but not direct task assignee
      if (assignedChecklists && assignedChecklists.length > 0) {
        if (isResumableInProgress) {
          return { task: shaped(), allowed: true };
        }
        const hasCanExecute = await currentUserCanExecuteTasks();
        if (hasCanExecute) {
          return { task: shaped(), allowed: true };
        }
      }

      return {
        task: shaped(),
        allowed: false,
        errorMessage: "You don't have permission to complete this task. Ask the owner to enable 'Can do tasks' for you.",
      };
    },
    enabled: !!propertyId && !!taskId && !!user?.id,
    staleTime: 0,
  });

  // A start or finish still waiting to sync (no signal) shows as done.
  const pendingTask = useWithPendingStamps(data?.task);

  return {
    task: pendingTask ?? null,
    allowed: data?.allowed ?? false,
    errorMessage: data?.errorMessage,
    isLoading,
  };
}
